import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Custom Hook for API-Driven Dropdowns
 * Ensures on-demand loading, caching, error handling, and cancellation
 */
export const useApiDropdown = ({ fetcher, normalizer, dep = null, enabled = true }) => {
  const [data, setData] = useState({
    options: [],
    loading: false,
    error: null,
    loaded: false
  });

  const loadingRef = useRef(false);
  const loadedRef = useRef(false);
  const depRef = useRef(dep);
  const abortControllerRef = useRef(null);

  // Reset state if dependency changes (e.g. parent category)
  useEffect(() => {
    if (depRef.current !== dep) {
      depRef.current = dep;
      loadedRef.current = false;
      loadingRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setData({
        options: [],
        loading: false,
        error: null,
        loaded: false
      });
    }
  }, [dep]);

  const loadOptions = useCallback(async (force = false) => {
    if (!enabled || !fetcher) return;
    if (loadedRef.current && !force) return;
    if (loadingRef.current && !force) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    loadingRef.current = true;
    setData((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const res = await fetcher({ signal: controller.signal });
      let items = [];
      if (res && res.success && Array.isArray(res.data)) {
        items = res.data;
      } else if (res && Array.isArray(res.data)) {
        items = res.data;
      } else if (Array.isArray(res)) {
        items = res;
      } else if (res && res.data && Array.isArray(res.data.results)) {
        items = res.data.results;
      } else if (res && res.data && Array.isArray(res.data.items)) {
        items = res.data.items;
      }

      const normalized = normalizer ? normalizer(items) : items;
      loadedRef.current = true;
      loadingRef.current = false;
      setData({
        options: Array.isArray(normalized) ? normalized : [],
        loading: false,
        error: null,
        loaded: true
      });
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Dropdown fetch error:', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to load options';
      loadingRef.current = false;
      loadedRef.current = false;
      setData((prev) => ({
        ...prev,
        loading: false,
        error: msg,
        loaded: false
      }));
    }
  }, [fetcher, normalizer, enabled]);

  const setOptionsDirectly = useCallback((newOptions) => {
    loadedRef.current = true;
    loadingRef.current = false;
    setData({
      options: Array.isArray(newOptions) ? newOptions : [],
      loading: false,
      error: null,
      loaded: true
    });
  }, []);

  return {
    options: data.options,
    loading: data.loading,
    error: data.error,
    loaded: data.loaded,
    loadOptions: () => loadOptions(false),
    retry: () => loadOptions(true),
    setOptionsDirectly
  };
};

export default useApiDropdown;
